import turfArea from '@turf/area';
import { units, convertWithUnits } from '@/utils/math-utils';
import result from 'lodash.result';
import { post } from '@/utils/api-utils';
import { to } from '@/utils/promise-utils';
//import { intVal } from '@/utils/string-utils';
import { maxStages, loadState, saveState, cloneDeep, log } from '@/utils';
import initialStages from '@/copy/initial-stages';
import finalStages from '@/copy/final-stages';

const defaultState = {
	initialStageIndex: 0,
	finalStageIndex: 0,
	modifiedStageIndex: null,		// if a stage is being manually modified afterwards
	maxStages: 20,
	schedule1Development: null,
	proposal: null,
	responses: [],
	consent: {
		privacyPolicy: false,
		termsOfUse: false,
		contact: false,
	},
	geoJSONBoundary: null,
	address: {
		addressLines: [],
		townCity: '',
		country: '',
		postcode: '',
		latitude: null,
		longitude: null,
	},
	manualAddress: {
		addressLines: [],
		townCity: '',
		country: '',
		postcode: '',
		latitude: null,
		longitude: null,
	},
	manualAddressValid: false,
	siteArea: 0, // Always stored as m2 (SQM)
	manualSiteArea: 0, // Always stored as m2 (SQM)
	siteAreaDisplayUnits: units.ACRES,
	calculatedSiteAreaIsCorrect: null,
	userDetails: {
		name: '',
		email: '',
		companyName: '',
	},
	userDetailsValid: false,
	calculationResults: {},
	outcome: null,
};

//console.log( maxStages( loadState( defaultState ) ) );

function modifiedResponseIndex( state ){
	return state.modifiedStageIndex !== null ? state.modifiedStageIndex - ( initialStages.length + 1 ) : null;
}

export default {
	namespaced: true,

	state: () => loadState( defaultState ),

	mutations: {

		setSchedule1Development( state, yes ){
			state.schedule1Development = yes;

			saveState( state );
		},

		clearLastResponse( state ){
			state.responses.pop();
			saveState( state );
		},

		clearChoice( state ){
			state.proposal = null;
			state.responses = [];
			saveState( state );
		},

		makeChoice( state, payload ) {

			if( payload.proposal ){

				// are we modifying the proposal we chose previously?
				if( state.proposal && state.modifiedStageIndex !== null ){

					// return to review stage
					state.modifiedStageIndex = null;

					// do we have a different proposal?
					// clear out responses as we need to do them again.
					if( state.proposal.id !== payload.proposal.id ){
						state.proposal = payload.proposal;
						state.responses = [];
					}

				}
				else{
					state.proposal = payload.proposal;
					state.responses = [];
				}
			}

			// no proposal? no point continuing
			if( !state.proposal ){
				return;
			}

			if( payload.response ){

				// replace instead of pushing if we are modifying
				if( state.modifiedStageIndex !== null ){

					const index = modifiedResponseIndex( state );

					// do we have an updated value?
					// if so, clear the subsequent responses
					if( ( payload.response.value !== state.responses[ index ].value ) || ( payload.response.operator !== state.responses[ index ].operator ) ){

						// value has changed, but result is the same
						// just swap the new response in
						if( payload.response.result === state.responses[ index ].result ){
							state.responses.splice( index, 1, payload.response );
						}

						// value AND result has changed, so subsequent responses are no-longer relevant
						else{
							// add preceding responses
							state.responses = [
								...state.responses.slice( 0, index ),
							];

							// append the modified response;
							state.responses.push( payload.response );
						}

					}

					state.modifiedStageIndex = null;

				}
				else{
					state.responses.push( payload.response 	);
				}
			}

			// calculate max number of stages
			state.maxStages = maxStages( state );

			saveState( state );
		},

		clearResponses( state ){
			state.responses = [];
			saveState( state );
		},

		storeGeoJSONBoundary( state, geoJSONBoundary ) {
			state.geoJSONBoundary = geoJSONBoundary;
			state.siteArea = turfArea( state.geoJSONBoundary );
			saveState( state );
		},

		setManualSiteArea( state, siteArea ) {
			state.manualSiteArea = convertWithUnits(
				siteArea,
				state.siteAreaDisplayUnits.id,
				units.SQM.id
			);

			saveState( state );
		},

		setAddress( state, addressModel ) {
			const manualAddress = state.manualAddress;

			manualAddress.addressLines = addressModel.addressLines.slice();
			manualAddress.townCity = addressModel.townCity;
			manualAddress.country = addressModel.country;
			manualAddress.postcode = addressModel.postcode;

			// No need to check for country value's existence here.
			// See AddressForm.vue.
			state.manualAddressValid = !!(
				Array.isArray( manualAddress.addressLines ) &&
				manualAddress.addressLines.length > 0 &&
				manualAddress.townCity &&
				manualAddress.postcode
			);

			saveState( state );
		},

		unsetAddress( state ) {
			state.manualAddress = {
				addressLines: [],
				townCity: '',
				country: '',
				postcode: '',
				latitude: null,
				longitude: null,
			};

			state.manualAddressValid = false;

			saveState( state );
		},

		setReverseGeocodeAddress( state, addressResponse ) {
			state.address = {
				addressLines: [
					result( addressResponse, 'address.houseNumber', null ),
					result( addressResponse, 'address.name', null ),
					result( addressResponse, 'address.road', null ),
				].filter( d => !!d ),
				townCity: result( addressResponse, 'address.city', null ),
				country: result( addressResponse, 'address.country', null ),
				postcode: result( addressResponse, 'address.postcode', null ),
				latitude: result( addressResponse, 'lat', null ),
				longitude: result( addressResponse, 'lon', null ),
			};

			saveState( state );
		},

		setGeocodeAddress( state, addressResponse ) {
			state.manualAddress.latitude = result( addressResponse, 'lat', null );
			state.manualAddress.longitude = result( addressResponse, 'lon', null );
			saveState( state );
		},

		setCalculatedSiteAreaIsCorrect( state, correct ) {
			state.calculatedSiteAreaIsCorrect = correct;
		},

		setUserName( state, name ) {
			state.userDetails.name = name;
			saveState( state );
		},

		setUserEmail( state, email ) {
			state.userDetails.email = email;
			saveState( state );
		},

		setUserCompanyName( state, companyName ) {
			state.userDetails.companyName = companyName;
			saveState( state );
		},

		setUserDetailsValid( state ) {
			state.userDetailsValid = !!(
				state.userDetails.name &&
				state.userDetails.email
			);

			saveState( state );
		},

		setConsent( state, consent ) {
			for( const k of Object.keys( state.consent ) ){
				if( Object.prototype.hasOwnProperty.call( consent, k ) ){
					state.consent[ k ] = consent[ k ];
				}
			}

			saveState( state );
		},

		setCalculationResults( state, results ) {
			state.calculationResults = results.calculation;
			state.outcome = results.outcome;
			saveState( state );
		},

		switchAreaDisplayUnits( state, id ){

			for( const unit of Object.values( units ) ){
				if( unit.id === id ){
					state.siteAreaDisplayUnits = unit;
				}
			}

			saveState( state );
		},

		incrementInitialStageIndex( state ){
			state.initialStageIndex = state.initialStageIndex + 1;
			saveState( state );
		},

		decrementInitialStageIndex( state ){
			if( state.initialStageIndex > 0 ){
				state.initialStageIndex = state.initialStageIndex - 1;
				saveState( state );
			}
		},

		incrementFinalStageIndex( state ){
			state.finalStageIndex = state.finalStageIndex + 1;
			saveState( state );
		},

		decrementFinalStageIndex( state ){
			if( state.finalStageIndex > 0 ){
				state.finalStageIndex = state.finalStageIndex - 1;
				saveState( state );
			}
		},

		modifyStageIndex( state, index ){
			state.modifiedStageIndex = index;
			saveState( state );
		},

		reset( state ){
			const newState = cloneDeep( defaultState );

			for( const key of Object.keys( newState ) ){
				state[ key ] = newState[ key ];
			}

			saveState( state );
		},
	},

	actions: {
		async getResults( { commit, getters } ) {
			const [ err, results ] = await to( post( 'submit', getters.getSubmissionPayload ) );

			if( err ) {
				console.error( err );

				throw err;
			}

			log( 'submit results', results );

			commit( 'setCalculationResults', results );
		},
	},

	getters: {

		totalStages( state ){

			return 1 + initialStages.length + state.maxStages + finalStages.length;

		},

		currentStageIndex( state, getters ) {
			if( state.modifiedStageIndex !== null ){
				return state.modifiedStageIndex;
			}

			return getters.completedStageIndex;

		},

		completedStageIndex( state ) {

			let index = state.initialStageIndex;

			if( state.proposal ){

				let n = state.responses.length + 1;

				// have we reached the end of questioning?
				if( ( state.responses.length && !Number.isInteger( state.responses[ state.responses.length - 1 ].result ) || !state.proposal.questions.length ) ){
					n += state.finalStageIndex;
				}

				index += n;
			}

			return index;
		},

		manualAddressArray( state ) {
			if( state.manualAddressValid === false ) {
				return [];
			}

			const parts = [
				...state.manualAddress.addressLines,
				state.manualAddress.townCity,
				state.manualAddress.country,
				state.manualAddress.postcode,
			];

			return parts.filter( d => !!d );
		},

		addressArray( state ) {
			const parts = [
				...state.address.addressLines,
				state.address.townCity,
				state.address.country,
				state.address.postcode,
			];

			return parts.filter( d => !!d );
		},

		geoJSONBoundary( state ) {
			return state.geoJSONBoundary;
		},

		siteAreaDisplayUnits( state ) {
			return state.siteAreaDisplayUnits;
		},

		manualAddressValid( state ) {
			return state.manualAddressValid;
		},

		hasSiteArea( state ){
			return !!( state.manualSiteArea || state.siteArea );
		},

		siteArea( state ) {
			if( !state.geoJSONBoundary ) {
				return null;
			}

			return state.siteArea;
		},

		manualSiteArea( state ) {
			return state.manualSiteArea;
		},

		displaySiteArea( state ) {
			if( !state.siteArea ) {
				return null;
			}

			return convertWithUnits( state.siteArea, units.SQM.id, state.siteAreaDisplayUnits.id );
		},

		displaySiteAreaUnified( state ) {
			const area = state.manualSiteArea || state.siteArea;

			return convertWithUnits( area, units.SQM.id, state.siteAreaDisplayUnits.id );
		},

		calculatedSiteAreaIsCorrect( state ) {
			return state.calculatedSiteAreaIsCorrect;
		},

		userDetails( state ) {
			return state.userDetails;
		},

		userDetailsValid( state ) {
			return state.userDetailsValid;
		},

		getSubmissionPayload( state ) {
			return {
				schedule1Development: state.schedule1Development,
				proposal: state.proposal,
				siteArea: state.manualSiteArea || state.siteArea,
				userDetails: state.userDetails,
				address: state.manualAddressValid ?
					state.manualAddress :
					state.address,

				// This isn't always set, but we need to make sure that
				// the call to JSON.stringify doesn't exclude this key.
				geoJSONBoundary: state.geoJSONBoundary || false,

				responses: state.responses,

				consent: state.consent,
			};
		},

		modifiedResponseIndex( state ){
			return modifiedResponseIndex( state );
		},

		modifiedFinalStageIndex( state, getters ){
			return getters.modifiedResponseIndex - state.responses.length;
		},
	},
};
